import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Avatar,
  Typography,
  Divider,
  Alert,
  Chip,
  Grid,
} from '@mui/material';
import {
  Person as PersonIcon,
  Microsoft as MicrosoftIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface UserProfileProps {
  open: boolean;
  onClose: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ open, onClose }) => {
  const { user, linkMicrosoftAccount, hasMicrosoftAccount } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [profileData, setProfileData] = useState({
    displayName: user?.attributes?.name || '',
    email: user?.email || '',
    phone: user?.attributes?.phone_number || '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Here you would typically call an API to update the user profile
      // For now, we'll just simulate the save
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('Profile updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkMicrosoft = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await linkMicrosoftAccount();
      setSuccess('Microsoft account linked successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to link Microsoft account');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {user?.attributes?.name ? getInitials(user.attributes.name) : <PersonIcon />}
          </Avatar>
          <Typography variant="h6">User Profile</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={profileData.displayName}
              onChange={handleInputChange}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={profileData.email}
              onChange={handleInputChange}
              margin="normal"
              disabled // Email typically shouldn't be editable
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={profileData.phone}
              onChange={handleInputChange}
              margin="normal"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Connected Accounts
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="space-between" py={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <MicrosoftIcon color="primary" />
            <Box>
              <Typography variant="body1">Microsoft Outlook</Typography>
              <Typography variant="body2" color="textSecondary">
                {hasMicrosoftAccount 
                  ? `Connected as ${user?.microsoftAccount?.username}`
                  : 'Not connected'
                }
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            {hasMicrosoftAccount ? (
              <Chip
                icon={<LinkIcon />}
                label="Connected"
                color="success"
                size="small"
              />
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={handleLinkMicrosoft}
                disabled={loading}
              >
                Connect
              </Button>
            )}
          </Box>
        </Box>

        {hasMicrosoftAccount && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Your Microsoft account is connected and ready for email processing.
            You can access your Outlook emails and calendar through this application.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveProfile}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfile;